<template>
  <div class="md-layout md-alignment-top-center md-gutter">
    <LightBox
      class="light-box"
      :images="lightImages"
      ref="lightbox"
      :show-caption="false"
      :show-light-box="false"
      :site-loading="imageLoading"
    ></LightBox>
    <template
      v-if="
        fetchedSinister &&
        fetchedQuantification &&
        processIsGreaterThan('VaCo') &&
        !loading
      "
    >
      <div class="md-layout-item md-size-100">
        <ConceptsTable
          :fetchedConcepts="fetchedConcepts"
          :isCP="processIs('CP')"
          :process="process"
          :currencies="fetchedCurrencies"
          :quantification="fetchedQuantification"
          :roadSinister="fetchedSinister"
          :disableAll="disableAll || loadingConcepts"
          @selectedConcept="selectedConcept"
          @saveChanges="saveConceptChanges"
          @toggleConceptStatus="toggleConceptStatus"
          @pendingChanges="(v) => (pendingChanges = v)"
        />
      </div>
      <div class="md-layout-item md-size-100">
        <FilesTab
          :fileTypes="fileTypes"
          :loadedFiles="files"
          :disableAll="disableAll"
          :casualty="casualty"
          @saveChanges="saveFilesChanges"
          @openVisor="openVisor"
        />
      </div>
    </template>
    <div v-else-if="fetchedSinister && processIsSmallerThan('LV') && !loading">
      <loading-content
        slot="empty-state"
        class="md-accent"
        empty-icon="report_problem"
        empty-label="Proceso incorrecto"
        :empty-description="`El siniestro se encuentra en ${process.name}`"
      />
    </div>
    <div v-else-if="!fetchedSinister && !loading">
      <loading-content
        slot="empty-state"
        class="md-accent"
        empty-icon="highlight_off"
        empty-label="Problema al cargar la página"
      />
    </div>
    <div
      v-if="fetchedSinister && !loading"
      class="md-layout-item md-layout md-alignment-center-right md-size-100"
    >
      <md-button
        v-if="
          fetchedQuantification &&
          processIs('LV') &&
          $can('change_process_to_VR', 'Sinister', 'affected')
        "
        @click="finishLV"
        class="md-success"
      >Terminar Levantamiento</md-button>
      <md-button
        :to="{
          name: 'Ficha de Siniestro Carretero',
          params: {
            id: this.fetchedSinister.id,
          },
        }"
      >Volver a ficha</md-button>
    </div>

    <FilesModal
      :dialog="dialogFiles"
      :fetchedFileTypes="fetchedFileTypes"
      :concept="currentConcept"
      :quotation="quotation"
      :disableAll="disableAll"
      :casualty="casualty"
      @closeDialog="closeDialog"
      @saveChanges="saveConceptFiles"
      @openVisor="openVisor"
    />
  </div>
</template>

<script>
/**
 * @author Jose Luis R
 */

import FilesModal from "./FilesModal.vue";
import ConceptsTable from "./ConceptsTable";
// import RequestClarification from "./RequestClarification.vue";
import FilesTab from "./FilesTab";

import LightBox from "vue-image-lightbox";
import imageLoading from "@/assets/images/loading.gif";

import sinisterService from "@/services/sinister";
import {
  fetchConcepts,
  finishLV,
  updateConcepts,
  rejectFiles,
  addConceptFile,
} from "@/services/purchase/lifting";
import fileService from "@/services/Files";
import catalogService from "@/services/catalog";
import workPlanService from "@/services/workPlan";
import purchaseService from "@/services/purchase";

import { mapPermissions } from "@/plugins/accountPermissions/";
import processCalculationsMixin from "@/pages/RoadSinisters/File/mixins/processCalculations";

export default {
  name: "Lifting",
  components: {
    ConceptsTable,
    FilesModal,
    LightBox,
    FilesTab,
  },
  props: {
    id: {
      type: [Number, String],
      required: true,
      default: "",
    },
    quotation: {
      type: [Number, String],
      required: true,
      default: "",
    },
  },
  data() {
    return {
      casualtyId: null,
      fetchedConcepts: [],
      fetchedFileTypes: [],
      fetchedCurrencies: [],
      fetchedSinister: null,
      fetchedQuantification: null,
      casualty: {},
      loading: true,
      loadingConcepts: false,
      dialogFiles: false,
      currentConcept: {},
      files: {},
      lightImages: [],
      pendingChanges: false,
      imageLoading,
    };
  },
  mixins: [processCalculationsMixin],
  computed: {
    ...mapPermissions("Quantification", "quantificationconcept", ["change"]),
    fileTypes() {
      const allowTypes = ["FPD", "BTC", "ENRAC"];
      return this.fetchedFileTypes
        .filter((t) => allowTypes.some((a) => a === t.codename))
        .sort(
          (a, b) =>
            allowTypes.indexOf(a.codename) - allowTypes.indexOf(b.codename)
        )
        .map((t) => {
          const { codename } = t;
          switch (codename) {
            case "ENRAC":
              return {
                ...t,
                allowedFormats: ["jpg", "jpeg"],
                type: "image",
                disableCharge: () => false,
              };
            case "FPD":
              return {
                ...t,
                type: "image",
                allowedFormats: ["jpg", "jpeg"],
                disableCharge: (files) => !!files.FPD && !!files.FPD.length,
                singleFile: true,
              };

            default:
              return {
                ...t,
                allowedFormats: ["pdf", "xlsx", "xls"],
                disableCharge: () => false,
              };
          }
        });
    },
    process() {
      const { process } = this.casualty;
      return process || { abbreviation: "sss" };
    },
    disableAll() {
      return (
        !this.processIsSmallerThan("ADO") ||
        !this.canChangeQuantificationconcept
      );
    },
  },
  methods: {
    saveFilesChanges(files = [], reject = [], visible = []) {
      let p1 = () =>
        !!files.length
          ? files.map((c) =>
              fileService.create({ ...c, affected: this.casualty.id })
            )
          : [];
      let p2 = () => !!reject.length && rejectFiles(reject);
      let p3 = () =>
        !!visible.length && workPlanService.updateCustomerVisibility(visible);
      const promise = new Promise((resolve) => {
        Promise.all([...p1(), p2(), p3()])
          .then(() => {
            fileService
              .fetch("file/getfile", {
                id: this.casualty.id,
                noStore: true,
                collection: true,
              })
              .then((res) => {
                const files = this.fileTypes.reduce((ac, a) => {
                  const filteredFiles = res
                    .filter((f) => f.codename === a.codename)
                    .sort((a, b) => a.id - b.id);
                  return { ...ac, [a.codename]: filteredFiles };
                }, {});
                this.files = Object.assign({}, this.file, files);
              });
            resolve();
          })
          .catch(this.$fireErrorResponse);
      });
      return promise;
    },
    saveConceptChanges(concepts = []) {
      this.loadingConcepts = true;
      const toUpdateConcepts = concepts.filter((c) => {
        if (!!c.backupQuantityExecuted && !(c.canNotBeRepaired && c.notToDo)) {
          return c.quantification;
        }
        return c.quantification && +c.quantityExecuted >= 0;
      });
      return updateConcepts(toUpdateConcepts)
        .then(() => {
          fetchConcepts({ id: this.quotation }).then((res) => {
            this.fetchedConcepts = res;
          });
        })
        .catch(this.$fireErrorResponse)
        .finally(() => (this.loadingConcepts = false));
    },
    toggleConceptStatus(concept, index) {
      this.loadingConcepts = true;
      return updateConcepts([concept])
        .then(() => {
          this.fetchedConcepts.splice(index, 1, concept);
        })
        .catch(this.$fireErrorResponse)
        .finally(() => (this.loadingConcepts = false));
    },
    saveConceptFiles(files = [], reject = [], visible = []) {
      let p1 = () =>
        files.length
          ? files.map((f) =>
              addConceptFile({
                ...f,
                concept: this.currentConcept.id,
              })
            )
          : [];
      let p2 = () => reject.length && rejectFiles(reject);
      let p3 = () =>
        !!visible.length && workPlanService.updateCustomerVisibility(visible);

      const promise = new Promise((resolve) => {
        Promise.all([...p1(), p2(), p3()])
          .then(() => {
            fetchConcepts({ id: this.quotation }).then((res) => {
              this.fetchedConcepts = res;
              this.currentConcept = {
                ...res.find((c) => c.id === this.currentConcept.id),
              };
            });
            resolve();
          })
          .catch(this.$fireErrorResponse);
      });
      return promise;
    },
    finishLV() {
      this.$loadingMessage();
      if (this.pendingChanges) {
        this.$alertMessage({
          title: "Hay cambios en los conceptos que aun no se han guardado",
          timer: 2000,
        });
        return;
      }
      return finishLV(this.casualty)
        .then(() => {
          this.goToSinisterFile();
          this.$successMessage({ title: "Proceso terminado" });
        })
        .catch(this.$fireErrorResponse);
    },
    selectedConcept(concept) {
      this.currentConcept = concept;
      this.dialogFiles = true;
    },
    closeDialog() {
      this.currentConcept = {};
      this.dialogFiles = false;
    },
    fileTransformer({
      id,
      file,
      visible_customer: visibleCustomer,
      is_active: isActive,
      file_type: fileType,
      affected,
      add_date: addDate,
    }) {
      let name = "";
      if (file) {
        name = file.split("/").pop();
      }
      return {
        id,
        file,
        affected: affected.id,
        fileType: fileType.file_type_codename,
        visibleCustomer,
        isActive,
        name,
        addDate,
      };
    },
    goToSinisterFile() {
      this.$router.push({
        name: "Ficha de Siniestro Carretero",
        params: {
          id: this.fetchedSinister.id,
        },
      });
    },
    openVisor(image) {
      const imageDisplay = {
        thumb: image.file,
        src: image.file,
      };
      this.lightImages = [imageDisplay];
      this.$refs.lightbox.showImage(0);
    },
  },
  mounted() {
    let { afectado: casualtyId } = this.$route.query;
    this.casualtyId = casualtyId;
    const promise = new Promise((resolve) => {
      Promise.all([
        sinisterService.fetch({ id: this.id }),
        fileService.fetch("filetype"),
        catalogService.fetch("currency"),
        purchaseService.fetchQuantification(casualtyId),
        fileService.fetch("file/getfile", {
          id: casualtyId,
          noStore: true,
          collection: true,
        }),
        fetchConcepts({ id: this.quotation }),
      ])
        .then(
          ([
            sinister,
            filetype,
            currencies,
            quantification,
            files,
            concepts,
          ]) => {
            this.fetchedSinister = { ...sinister };
            const { affected } = sinister;
            this.casualty = affected;
            this.fetchedFileTypes = filetype;
            this.fetchedCurrencies = currencies;
            this.fetchedQuantification = quantification;
            const filesMap = this.fileTypes.reduce((ac, a) => {
              const filteredFiles = files
                .filter((f) => f.codename === a.codename)
                .sort((a, b) => a.id - b.id);
              return { ...ac, [a.codename]: filteredFiles };
            }, {});
            this.files = Object.assign({}, this.files, filesMap);
            this.fetchedConcepts = concepts;
            resolve();
          }
        )
        .catch(this.$fireErrorResponse)
        .finally(() => (this.loading = false));
    });
    return promise;
  },
};
</script>

<styles lang="scss" scoped>
.light-box {
  z-index: 10000;
}
</styles>



// WEBPACK FOOTER //
// src/pages/RoadSinisters/File/Lifting/index.vue